"use client";

import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import {
	ArrowLeft,
	ArrowRight,
	Camera,
	CheckCircle2,
	Clock,
	CreditCard,
	Home,
	Image as ImageIcon,
	MapPin,
	Building,
	ShoppingCart,
	X,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import {
	createImageryCheckoutSessionAuth,
	type ImageryLineItem,
	imageryCheckIn,
} from "@/services/imagery-api";
import { checkPropertyMediaRequest } from "@/services/property-media-api";
import { getUserDetails } from "@/services/stripe-api";
import {
	getImagerySessionCookie,
	setImagerySessionCookie,
} from "@/lib/cookies";

/** Circular black back / close control used across modal steps */
const MODAL_ROUND_BACK_BUTTON_CLASS =
	"inline-flex size-12 shrink-0 items-center justify-center rounded-full bg-[#16251c] text-white shadow-md shadow-[#16251c]/35 hover:bg-black hover:shadow-lg hover:shadow-black/40 active:scale-[0.96] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#16251c] disabled:hover:shadow-md";

export interface RequestPhotosPaymentProperty {
	id: string;
	address: string;
	city: string;
	state: string;
	county: string;
	zip: string;
	latitude: number;
	longitude: number;
}

export interface RequestPhotosPaymentModalProps {
	isOpen: boolean;
	onClose: () => void;
	properties: RequestPhotosPaymentProperty[];
	/** Called when the modal closes (so parent can persist removals / edits to a draft order). */
	onCloseWithSelection?: (properties: RequestPhotosPaymentProperty[]) => void;
	/**
	 * When set (e.g. row camera on dashboard), shows “Add to order” on the info step:
	 * merges into the parent draft, closes the modal, and skips `onCloseWithSelection`.
	 */
	onAddToOrderAndClose?: (properties: RequestPhotosPaymentProperty[]) => void;
	/**
	 * When set (e.g. guest imagery checkout), replaces the default Pay handler (property-media precheck + auth Stripe).
	 * Parent is responsible for starting checkout and redirecting (e.g. `createImageryCheckoutSession`).
	 */
	onPaySelected?: (
		properties: RequestPhotosPaymentProperty[]
	) => Promise<void>;
}

type ModalStep = "info" | "confirm";

export default function RequestPhotosPaymentModal({
	isOpen,
	onClose,
	properties,
	onCloseWithSelection,
	onAddToOrderAndClose,
	onPaySelected,
}: RequestPhotosPaymentModalProps) {
	const [currentStep, setCurrentStep] = useState<ModalStep>("info");
	const [isProcessing, setIsProcessing] = useState(false);
	const [selectedProperties, setSelectedProperties] =
		useState<RequestPhotosPaymentProperty[]>(properties);

	const PRICE_PER_PROPERTY = 25;
	const totalProperties = selectedProperties.length;
	const totalAmount = PRICE_PER_PROPERTY * totalProperties;

	// Reset modal state when opening.
	useEffect(() => {
		if (!isOpen) return;
		setSelectedProperties(properties);
		setCurrentStep("info");
		setIsProcessing(false);
	}, [isOpen, properties]);

	const handleClose = (opts?: { skipDraftSync?: boolean }) => {
		if (isProcessing) return; // Keep the modal open during processing.
		if (!opts?.skipDraftSync) {
			onCloseWithSelection?.(selectedProperties);
		}
		onClose();
	};

	const handleAddToOrder = () => {
		if (selectedProperties.length === 0 || isProcessing) return;
		onAddToOrderAndClose?.(selectedProperties);
		handleClose({ skipDraftSync: true });
	};

	const handleRemoveProperty = (indexToRemove: number) => {
		setSelectedProperties((prev) =>
			prev.filter((_, index) => index !== indexToRemove)
		);
	};

	const handleConfirm = () => {
		if (selectedProperties.length === 0) return;
		setCurrentStep("confirm");
	};

	const progressSteps = [
		{ number: 1, title: "Confirm Property", step: "confirm" as const },
		{ number: 2, title: "Pay for Request", step: "confirm" as const },
		{ number: 3, title: "Request Submitted", step: "success" as const },
	];

	const getStepIndex = (step: ModalStep) => {
		if (step === "info") return 0;
		if (step === "confirm") return 1;
		return 0;
	};

	const currentStepIndex = getStepIndex(currentStep);

	const handlePay = async () => {
		if (isProcessing) return;
		if (selectedProperties.length === 0) return;

		setIsProcessing(true);
		try {
			if (onPaySelected) {
				await onPaySelected(selectedProperties);
				return;
			}

			// Mirror "Camera" logic: only allow properties that can still request.
			const results = await Promise.all(
				selectedProperties.map(async (p) => {
					const res = await checkPropertyMediaRequest(p.id);
					return { property: p, canRequest: res.canRequest };
				})
			);

			const canRequest = results
				.filter((r) => r.canRequest)
				.map((r) => r.property);

			if (canRequest.length === 0) {
				toast.info(
					"Request already initiated for all selected properties."
				);
				setSelectedProperties([]);
				setCurrentStep("info");
				setIsProcessing(false);
				return;
			}

			if (canRequest.length !== selectedProperties.length) {
				toast.info(
					"Some properties already have requests and were removed from this payment."
				);
				setSelectedProperties(canRequest);
			}

			const user = await getUserDetails();
			const email = user?.email?.trim() ?? "";
			if (!email.includes("@")) {
				throw new Error("Unable to determine your email for checkout.");
			}

			// Ensure the imagery session cookie exists so `/dashboard` can fetch the
			// order via `getImageryOrder(...)` (which requires the public entity id).
			const sessionCookie = getImagerySessionCookie();
			if (!sessionCookie?.enrichmentPublicEntityId) {
				const res = await imageryCheckIn(email);
				setImagerySessionCookie(email, res.enrichmentPublicEntityId);
			}

			const origin =
				typeof window !== "undefined" ? window.location.origin : "";

			const lineItems: ImageryLineItem[] = canRequest.map((p) => ({
				real_estate_id: p.id,
				address: p.address,
				city: p.city,
				state: p.state,
				county: p.county,
				zip: p.zip,
				lat: p.latitude,
				lng: p.longitude,
			}));

			const { url } = await createImageryCheckoutSessionAuth({
				email,
				successUrl: `${origin}/dashboard?payment=success`,
				cancelUrl: `${origin}/dashboard?payment=failed`,
				lineItems,
			});

			window.location.href = url;
		} catch (err) {
			console.error("Failed to start checkout", err);
			toast.error(
				err instanceof Error
					? err.message
					: "Failed to start payment. Please try again."
			);
			setIsProcessing(false);
		}
	};

	const steps = [
		{
			icon: Camera,
			title: "You Raise a Request",
			description:
				"Submit your property photo request through our platform with just one click.",
			color: "text-[#A7FF5E]",
			bgColor: "bg-[#A7FF5E]/10",
		},
		{
			icon: MapPin,
			title: "We Visit the Site",
			description:
				"Our professional photographers visit the property location in person.",
			color: "text-[#4ade80]",
			bgColor: "bg-[#4ade80]/10",
		},
		{
			icon: ImageIcon,
			title: "Real Photos Captured",
			description:
				"We capture high-quality, ground-level images of the actual property—no satellite views.",
			color: "text-[#22c55e]",
			bgColor: "bg-[#22c55e]/10",
		},
		{
			icon: CheckCircle2,
			title: "Delivered to You",
			description:
				"Photos are processed and delivered directly to your account within 24-48 hours.",
			color: "text-[#16a34a]",
			bgColor: "bg-[#16a34a]/10",
		},
	] as const;

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[10060] flex items-center justify-center p-4">
			{/* Backdrop */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				className="absolute inset-0 bg-black/60 backdrop-blur-sm"
				onClick={() => handleClose()}
			/>

			{/* Modal */}
			<motion.div
				initial={{ opacity: 0, scale: 0.95, y: 20 }}
				animate={{ opacity: 1, scale: 1, y: 0 }}
				exit={{ opacity: 0, scale: 0.95, y: 20 }}
				transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
				// Compress X/Y size by ~15% to avoid colliding with the dashboard header.
				className="relative bg-white rounded-2xl shadow-2xl max-w-7xl w-[85%] h-[76.5vh] overflow-hidden flex flex-col"
				onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
			>
				<AnimatePresence mode="wait">
					{/* Info Step */}
					{currentStep === "info" && (
						<motion.div
							key="info"
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: 20 }}
							transition={{ duration: 0.3 }}
							className="flex flex-col h-full"
						>
							{/* Header */}
							<div className="relative bg-gradient-to-br from-[#16251c] to-[#0f1b13] px-8 py-6 flex-shrink-0">
								<button
									onClick={() => handleClose()}
									type="button"
									aria-label="Close"
									className={`absolute right-6 top-6 z-10 ${MODAL_ROUND_BACK_BUTTON_CLASS}`}
								>
									<ArrowLeft
										className="w-6 h-6 shrink-0"
										strokeWidth={2.25}
										aria-hidden
									/>
								</button>

								<div className="flex items-start gap-4">
									<div className="bg-[#A7FF5E] rounded-xl p-3">
										<Camera className="w-8 h-8 text-[#16251c]" />
									</div>
									<div>
										<h2
											data-testid="request-photos-modal-title"
											className="text-3xl font-bold text-white mb-2"
										>
											Request Real Property Photos
										</h2>
										<p
											data-testid="request-photos-modal-subtitle"
											className="text-white/80 text-base"
										>
											Professional, ground-level
											photography delivered in 24-48 hours
										</p>
									</div>
								</div>
							</div>

							{/* Two Column Layout */}
							<div className="flex flex-1 min-h-0">
								{/* LEFT: Selected Properties List (Scrollable) */}
								<div className="flex-1 flex flex-col border-r border-gray-200">
									{/* Section Header */}
									<div className="px-8 py-5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
										<h3
											data-testid="selected-properties-title"
											className="text-lg font-semibold text-gray-900"
										>
											Selected Properties (
											{totalProperties})
										</h3>
										<p className="text-sm text-gray-600 mt-1">
											Review the properties you&apos;ve
											selected for photo requests
										</p>
									</div>

									{/* Scrollable Properties List */}
									<div className="flex-1 px-8 py-6 overflow-y-auto">
										{totalProperties === 0 ? (
											<div className="flex flex-col items-center justify-center h-full py-12">
												<div className="bg-gray-100 rounded-full p-6 mb-4">
													<Home className="w-12 h-12 text-gray-400" />
												</div>
												<h3 className="text-lg font-semibold text-gray-900 mb-2">
													No Properties Selected
												</h3>
												<p className="text-sm text-gray-600 text-center max-w-xs">
													All properties have been
													removed. Please close and
													select properties again.
												</p>
											</div>
										) : (
											<AnimatePresence mode="popLayout">
												<div className="space-y-3">
													{selectedProperties.map(
														(property, index) => (
															<motion.div
																key={`property-info-${index}-${property.id}`}
																layout
																initial={{
																	opacity: 0,
																	scale: 0.9,
																}}
																animate={{
																	opacity: 1,
																	scale: 1,
																}}
																exit={{
																	opacity: 0,
																	scale: 0.9,
																	x: -100,
																}}
																transition={{
																	type: "spring",
																	stiffness: 300,
																	damping: 30,
																}}
																className="bg-white border border-gray-200 hover:border-[#A7FF5E] rounded-xl p-4 transition-all hover:shadow-md group relative"
															>
																<div className="flex items-start gap-4">
																	{/* Property Icon */}
																	<div className="bg-gradient-to-br from-[#A7FF5E]/20 to-[#cbf7a6]/20 rounded-lg p-3 flex-shrink-0 group-hover:from-[#A7FF5E]/30 group-hover:to-[#cbf7a6]/30 transition-all">
																		<Home className="w-6 h-6 text-[#16251c]" />
																	</div>

																	{/* Property Details */}
																	<div className="flex-1 min-w-0">
																		<div className="flex items-start justify-between gap-3 mb-2">
																			<h4
																				data-testid="selected-property-address"
																				className="font-semibold text-gray-900 text-base leading-tight flex-1"
																			>
																				{
																					property.address
																				}
																			</h4>
																			<div className="flex items-center gap-2 flex-shrink-0">
																				<span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
																					#
																					{index +
																						1}
																				</span>
																				<button
																					type="button"
																					onClick={() =>
																						handleRemoveProperty(
																							index
																						)
																					}
																					className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 flex items-center justify-center transition-all hover:scale-110 border border-red-200"
																					title="Remove property"
																				>
																					<X
																						className="w-4 h-4"
																						strokeWidth={
																							2.5
																						}
																					/>
																				</button>
																			</div>
																		</div>
																		<div className="flex items-center gap-4 text-sm text-gray-600">
																			<div className="flex items-center gap-1.5">
																				<Building className="w-4 h-4 text-gray-400" />
																				<span className="font-mono text-xs">
																					{
																						property.id
																					}
																				</span>
																			</div>
																			<div className="flex items-center gap-1.5">
																				<MapPin className="w-4 h-4 text-gray-400" />
																				<span className="text-xs">
																					Property
																					Location
																				</span>
																			</div>
																		</div>
																	</div>
																</div>
															</motion.div>
														)
													)}
												</div>
											</AnimatePresence>
										)}
									</div>
								</div>

								{/* RIGHT: Process & Pricing */}
								<div className="w-[480px] flex flex-col bg-gradient-to-br from-gray-50 to-white">
									<div className="flex-1 px-8 py-6 overflow-y-auto">
										{/* How It Works */}
										<div className="mb-8">
											<h3
												data-testid="how-it-works-title"
												className="text-xl font-bold text-gray-900 mb-6"
											>
												How It Works
											</h3>

											<div className="space-y-4">
												{steps.map((step, index) => (
													<motion.div
														key={index}
														initial={{
															opacity: 0,
															x: 20,
														}}
														animate={{
															opacity: 1,
															x: 0,
														}}
														transition={{
															delay:
																0.1 +
																index * 0.1,
														}}
														className="flex gap-4"
													>
														<div
															className={`${step.bgColor} ${step.color} rounded-xl p-3 h-fit`}
														>
															<step.icon className="w-5 h-5" />
														</div>
														<div className="flex-1">
															<div className="flex items-center gap-2 mb-1">
																<span className="text-xs font-bold text-white bg-gray-900 px-2 py-0.5 rounded-full">
																	STEP{" "}
																	{index + 1}
																</span>
															</div>
															<h4
																data-testid="how-it-works-step-title"
																className="font-semibold text-gray-900 text-sm mb-1"
															>
																{step.title}
															</h4>
															<p className="text-sm text-gray-600 leading-relaxed">
																{
																	step.description
																}
															</p>
														</div>
													</motion.div>
												))}
											</div>
										</div>

										{/* Pricing Summary */}
										<div className="bg-white border-2 border-[#A7FF5E] rounded-2xl p-6 shadow-lg">
											<h3
												data-testid="pricing-summary-title"
												className="text-lg font-bold text-gray-900 mb-4"
											>
												Pricing Summary
											</h3>

											<div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
												<div className="flex items-center justify-between text-sm">
													<span className="text-gray-600">
														Properties Selected
													</span>
													<span className="font-semibold text-gray-900">
														{totalProperties}
													</span>
												</div>
												<div className="flex items-center justify-between text-sm">
													<span className="text-gray-600">
														Price per Property
													</span>
													<span className="font-semibold text-gray-900">
														${PRICE_PER_PROPERTY}.00
													</span>
												</div>
											</div>

											<div className="flex items-center justify-between">
												<span className="text-lg font-bold text-gray-900">
													Total Amount
												</span>
												<div className="text-right">
													<div className="text-3xl font-bold text-[#16251c]">
														${totalAmount}.00
													</div>
													<div className="text-xs text-gray-500">
														USD
													</div>
												</div>
											</div>
										</div>

										{/* Delivery Notice */}
										<div className="mt-6 bg-[#A7FF5E]/10 border border-[#A7FF5E]/30 rounded-xl p-4">
											<div className="flex items-start gap-3">
												<Clock className="w-5 h-5 text-[#16251c] mt-0.5 flex-shrink-0" />
												<div>
													<p
														data-testid="fast-delivery-title"
														className="font-semibold text-sm text-[#16251c] mb-1"
													>
														Fast Delivery Guaranteed
													</p>
													<p className="text-xs text-[#16251c]/80 leading-relaxed">
														All photos are
														professionally processed
														and delivered within
														24-48 hours of your
														request.
													</p>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Footer — compact: secondary actions left, Continue right */}
							<div className="px-8 py-5 bg-gray-50 border-t border-gray-200 flex flex-shrink-0 flex-wrap items-center justify-between gap-3">
								<div className="flex flex-wrap items-center gap-3">
									<button
										type="button"
										onClick={() => handleClose()}
										aria-label="Close"
										className={
											MODAL_ROUND_BACK_BUTTON_CLASS
										}
									>
										<ArrowLeft
											className="w-6 h-6 shrink-0"
											strokeWidth={2.25}
											aria-hidden
										/>
									</button>
									{onAddToOrderAndClose && (
										<button
											type="button"
											onClick={handleAddToOrder}
											disabled={totalProperties === 0}
											className="inline-flex shrink-0 items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-base font-semibold text-[#16251c] bg-white border-2 border-[#12301b] shadow-md shadow-gray-900/12 hover:shadow-lg hover:shadow-[#16251c]/20 hover:bg-[#A7FF5E]/15 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-md"
										>
											<ShoppingCart
												className="w-5 h-5 shrink-0"
												aria-hidden
											/>
											Add to order
										</button>
									)}
								</div>
								<button
									type="button"
									data-testid="request-photos-continue-payment-button"
									onClick={handleConfirm}
									disabled={totalProperties === 0}
									className="group inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base font-semibold text-[#16251c] bg-[#A7FF5E] border border-[#12301b]/15 shadow-lg shadow-[#16251c]/20 hover:shadow-xl hover:shadow-[#16251c]/28 hover:bg-[#cbf7a6] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-md"
								>
									Continue
									<ArrowRight
										className="w-5 h-5 shrink-0 group-hover:translate-x-0.5 transition-transform"
										aria-hidden
									/>
								</button>
							</div>
						</motion.div>
					)}

					{/* Confirmation Step */}
					{currentStep === "confirm" && (
						<motion.div
							key="confirm"
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: 20 }}
							transition={{ duration: 0.3 }}
							className="flex flex-col h-full"
						>
							{/* Header with Step Progress */}
							<div className="relative bg-gradient-to-br from-[#16251c] to-[#0f1b13] px-8 py-6 flex-shrink-0">
								<button
									type="button"
									onClick={() => handleClose()}
									aria-label="Close"
									className={`absolute right-6 top-6 z-10 ${MODAL_ROUND_BACK_BUTTON_CLASS}`}
								>
									<ArrowLeft
										className="w-6 h-6 shrink-0"
										strokeWidth={2.25}
										aria-hidden
									/>
								</button>

								{/* Progress Steps */}
								<div className="mb-4">
									<div className="flex items-center justify-between max-w-2xl mx-auto">
										{progressSteps.map((step, index) => (
											<div
												key={index}
												className="flex items-center flex-1"
											>
												<div className="flex flex-col items-center flex-1">
													{/* Step Circle */}
													<div
														className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
															index <=
															currentStepIndex
																? "bg-[#A7FF5E] text-[#16251c]"
																: "bg-white/10 text-white/40"
														}`}
													>
														{index <
														currentStepIndex ? (
															<CheckCircle2 className="w-5 h-5" />
														) : (
															step.number
														)}
													</div>
													{/* Step Label */}
													<span
														className={`mt-2 text-xs text-center font-medium transition-all ${
															index <=
															currentStepIndex
																? "text-white"
																: "text-white/40"
														}`}
													>
														{step.title}
													</span>
												</div>
												{/* Connecting Line */}
												{index <
													progressSteps.length -
														1 && (
													<div
														className={`h-0.5 flex-1 mx-2 mb-8 transition-all ${
															index <
															currentStepIndex
																? "bg-[#A7FF5E]"
																: "bg-white/10"
														}`}
													/>
												)}
											</div>
										))}
									</div>
								</div>
							</div>

							{/* Two Column Layout */}
							<div className="flex flex-1 min-h-0">
								{/* LEFT: Selected Properties List (Scrollable) */}
								<div className="flex-1 flex flex-col border-r border-gray-200">
									{/* Section Header */}
									<div className="px-8 py-5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
										<h3
											className="text-lg font-semibold text-gray-900"
											data-testid="review-order-title"
										>
											Review Your Order ({totalProperties}{" "}
											{totalProperties === 1
												? "Property"
												: "Properties"}
											)
										</h3>
										<p className="text-sm text-gray-600 mt-1">
											Verify all property details before
											submitting your request
										</p>
									</div>

									{/* Scrollable Properties List */}
									<div className="flex-1 px-8 py-6 overflow-y-auto">
										<div className="space-y-3 mb-6">
											{selectedProperties.map(
												(property, index) => (
													<motion.div
														key={index}
														initial={{
															opacity: 0,
															y: 10,
														}}
														animate={{
															opacity: 1,
															y: 0,
														}}
														transition={{
															delay: index * 0.05,
														}}
														className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 shadow-sm"
													>
														<div className="flex items-start gap-4">
															{/* Property Icon */}
															<div className="bg-gradient-to-br from-[#A7FF5E] to-[#cbf7a6] rounded-lg p-3 flex-shrink-0">
																<Home className="w-6 h-6 text-[#16251c]" />
															</div>

															{/* Property Details */}
															<div className="flex-1 min-w-0">
																<div className="flex items-start justify-between gap-2 mb-3">
																	<div className="flex-1">
																		<div className="flex items-center gap-2 mb-1">
																			<span className="text-xs font-bold text-white bg-[#16251c] px-2 py-0.5 rounded-full">
																				#
																				{index +
																					1}
																			</span>
																		</div>
																		<h4 className="font-bold text-gray-900 text-base leading-tight mb-2">
																			{
																				property.address
																			}
																		</h4>
																	</div>
																	<div className="bg-green-100 text-green-700 rounded-lg px-3 py-1 text-xs font-semibold flex-shrink-0">
																		$
																		{
																			PRICE_PER_PROPERTY
																		}
																	</div>
																</div>

																{/* Property Info Grid */}
																<div className="grid grid-cols-2 gap-3 bg-white rounded-lg p-3 border border-gray-100">
																	<div>
																		<p className="text-xs text-gray-500 mb-1">
																			Property
																			ID
																		</p>
																		<p className="text-sm font-semibold text-gray-900 font-mono">
																			{
																				property.id
																			}
																		</p>
																	</div>
																	<div>
																		<p className="text-xs text-gray-500 mb-1">
																			Delivery
																			Time
																		</p>
																		<p className="text-sm font-semibold text-gray-900">
																			24-48
																			hours
																		</p>
																	</div>
																</div>
															</div>
														</div>
													</motion.div>
												)
											)}
										</div>

										{/* What You'll Get */}
										<div className="bg-[#A7FF5E]/10 border-2 border-[#A7FF5E]/30 rounded-xl p-5">
											<div className="flex items-center gap-2 mb-4">
												<div className="bg-[#16251c] rounded-lg p-2">
													<CheckCircle2 className="w-5 h-5 text-[#A7FF5E]" />
												</div>
												<h4 className="font-bold text-[#16251c] text-base">
													What You&apos;ll Receive
												</h4>
											</div>
											<ul className="space-y-3">
												{[
													"Professional ground-level photographs",
													"Multiple angles and perspectives",
													"High-resolution images",
													"Delivered within 24-48 hours",
												].map((item, index) => (
													<li
														key={index}
														className="flex items-start gap-3"
													>
														<CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
														<span className="text-sm text-[#16251c]/90 leading-relaxed font-medium">
															{item}
														</span>
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>

								{/* RIGHT: Payment Summary */}
								<div className="w-[480px] flex flex-col bg-gradient-to-br from-gray-50 to-white">
									<div className="flex-1 px-8 py-6 overflow-y-auto">
										<h3
											className="text-2xl font-bold text-gray-900 mb-6"
											data-testid="payment-summary-title"
										>
											Payment Summary
										</h3>

										{/* Price Breakdown */}
										<div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
											<div className="space-y-4 mb-5 pb-5 border-b border-gray-200">
												<div className="flex items-center justify-between">
													<span className="text-sm text-gray-600 font-medium">
														Property Photo Requests
													</span>
													<span className="font-bold text-gray-900">
														{totalProperties} ×
													</span>
												</div>
												<div className="flex items-center justify-between">
													<span className="text-sm text-gray-600 font-medium">
														Price per property
													</span>
													<span className="font-bold text-gray-900">
														${PRICE_PER_PROPERTY}.00
													</span>
												</div>
											</div>

											<div className="flex items-center justify-between">
												<span className="text-xl font-bold text-gray-900">
													Total Amount
												</span>
												<div className="text-right">
													<div className="text-4xl font-bold text-[#16251c]">
														${totalAmount}.00
													</div>
													<div className="text-xs text-gray-500 mt-1">
														USD
													</div>
												</div>
											</div>
										</div>

										{/* Payment Info Notice */}
										<div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mb-6">
											<div className="flex items-start gap-3">
												<div className="bg-blue-600 rounded-lg p-2 flex-shrink-0">
													<CreditCard className="w-5 h-5 text-white" />
												</div>
												<div className="flex-1">
													<p className="font-bold text-sm text-blue-900 mb-2">
														Secure Payment
													</p>
													<p className="text-xs text-blue-800 leading-relaxed">
														Your payment information
														is encrypted and secure.
														You&apos;ll only be
														charged once your
														request is confirmed.
													</p>
												</div>
											</div>
										</div>

										{/* Delivery Guarantee */}
										<div className="bg-[#A7FF5E]/10 border-2 border-[#A7FF5E]/30 rounded-xl p-5">
											<div className="flex items-start gap-3">
												<div className="bg-[#16251c] rounded-lg p-2 flex-shrink-0">
													<Clock className="w-5 h-5 text-[#A7FF5E]" />
												</div>
												<div className="flex-1">
													<p
														data-testid="confirm-delivery-title"
														className="font-bold text-sm text-[#16251c] mb-2"
													>
														24-48 Hour Delivery
													</p>
													<p className="text-xs text-[#16251c]/80 leading-relaxed">
														Photos are
														professionally captured
														and processed for
														delivery within 24-48
														hours of payment
														confirmation.
													</p>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Footer */}
							<div className="px-8 py-5 bg-gray-50 border-t border-gray-200 flex flex-shrink-0 flex-wrap items-center justify-between gap-3">
								<button
									type="button"
									onClick={() => setCurrentStep("info")}
									aria-label="Back"
									data-testid="confirm-back-button"
									disabled={isProcessing}
									className={MODAL_ROUND_BACK_BUTTON_CLASS}
								>
									<ArrowLeft
										className="w-6 h-6 shrink-0"
										strokeWidth={2.25}
										aria-hidden
									/>
								</button>
								<button
									type="button"
									onClick={handlePay}
									disabled={isProcessing}
									className="flex-1 min-w-[min(100%,16rem)] rounded-xl border-2 border-[#A7FF5E] bg-gradient-to-r from-[#16251c] to-[#0f1b13] px-6 py-3 font-semibold text-white shadow-lg transition-all hover:border-[#cbf7a6] hover:from-[#1f3128] hover:to-[#16251c] hover:shadow-xl group flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:border-gray-200 disabled:opacity-50"
								>
									{isProcessing ? (
										<>
											<motion.div
												animate={{ rotate: 360 }}
												transition={{
													duration: 1,
													repeat: Infinity,
													ease: "linear",
												}}
											>
												<Camera className="w-5 h-5" />
											</motion.div>
											Processing Payment...
										</>
									) : (
										<>
											<CreditCard className="w-5 h-5" />
											Pay ${totalAmount}.00 & Submit
											Request
										</>
									)}
								</button>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>
		</div>
	);
}
